/**
 * Stack Component
 *
 * A flexible flexbox-based layout component for arranging children
 * in a horizontal or vertical stack with consistent spacing.
 *
 * Uses CSS custom properties (design tokens) for spacing values,
 * ensuring consistency across the design system.
 *
 * @module components/core/Stack
 *
 * @example
 * ```tsx
 * // Vertical stack (default)
 * <Stack gap="md">
 *   <Card>Item 1</Card>
 *   <Card>Item 2</Card>
 * </Stack>
 *
 * // Horizontal stack with alignment
 * <Stack direction="row" gap="sm" align="center" justify="between">
 *   <Logo />
 *   <Navigation />
 *   <UserMenu />
 * </Stack>
 *
 * // Responsive wrapping layout
 * <Stack direction="row" wrap gap="md">
 *   {items.map(item => <Card key={item.id} />)}
 * </Stack>
 *
 * // Polymorphic usage
 * <Stack as="nav" direction="row" gap="lg">
 *   <NavLink>Home</NavLink>
 *   <NavLink>About</NavLink>
 * </Stack>
 * ```
 */

import { type ElementType, type ReactNode, type CSSProperties, forwardRef } from 'react';
import { cn } from '@/lib/utils';

/**
 * Gap size options mapped to CSS custom properties
 */
export type StackGap = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

/**
 * Flex direction options
 */
export type StackDirection = 'row' | 'column' | 'row-reverse' | 'column-reverse';

/**
 * Align items options (cross-axis alignment)
 */
export type StackAlign = 'start' | 'center' | 'end' | 'stretch' | 'baseline';

/**
 * Justify content options (main-axis alignment)
 */
export type StackJustify = 'start' | 'center' | 'end' | 'between' | 'around' | 'evenly';

/**
 * Flex wrap options
 */
export type StackWrap = boolean | 'wrap' | 'nowrap' | 'wrap-reverse';

/**
 * Props for the Stack component
 */
export interface StackProps {
  /**
   * Child elements to render in the stack
   */
  children: ReactNode;

  /**
   * Flex direction - determines how children are laid out
   * @default 'column'
   */
  direction?: StackDirection;

  /**
   * Gap between children - uses design system spacing tokens
   * @default 'md'
   */
  gap?: StackGap;

  /**
   * Responsive gap for medium screens (md breakpoint).
   * Note: This prop is accepted but ignored. Use className with
   * responsive Tailwind classes (e.g., md:gap-token-lg) instead.
   */
  gapMd?: StackGap;

  /**
   * Responsive gap for large screens (lg breakpoint).
   * Note: This prop is accepted but ignored. Use className with
   * responsive Tailwind classes (e.g., lg:gap-token-xl) instead.
   */
  gapLg?: StackGap;

  /**
   * Alignment of children along the cross axis
   * @default 'stretch'
   */
  align?: StackAlign;

  /**
   * Distribution of children along the main axis
   * @default 'start'
   */
  justify?: StackJustify;

  /**
   * Whether children should wrap to new lines
   * @default false
   */
  wrap?: StackWrap;

  /**
   * The HTML element or React component to render as
   * @default 'div'
   */
  as?: ElementType;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Inline styles (use sparingly - prefer tokens)
   */
  style?: CSSProperties;

  /**
   * Whether the stack should take full width
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Whether the stack should take full height
   * @default false
   */
  fullHeight?: boolean;

  /**
   * Optional data-testid for testing
   */
  'data-testid'?: string;
}

/**
 * Map gap sizes to CSS custom property values with fallbacks
 * Fallbacks ensure gaps work even if CSS variables aren't loaded yet
 */
const gapMap: Record<StackGap, string> = {
  none: '0',
  xs: 'var(--spacing-xs, 0.25rem)',
  sm: 'var(--spacing-sm, 0.5rem)',
  md: 'var(--spacing-md, 1rem)',
  lg: 'var(--spacing-lg, 1.5rem)',
  xl: 'var(--spacing-xl, 2rem)',
  '2xl': 'var(--spacing-2xl, 3rem)',
  '3xl': 'var(--spacing-3xl, 4rem)',
};

/**
 * Map direction values to Tailwind classes
 */
const directionMap: Record<StackDirection, string> = {
  row: 'flex-row',
  column: 'flex-col',
  'row-reverse': 'flex-row-reverse',
  'column-reverse': 'flex-col-reverse',
};

/**
 * Map align values to Tailwind classes
 */
const alignMap: Record<StackAlign, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
  baseline: 'items-baseline',
};

/**
 * Map justify values to Tailwind classes
 */
const justifyMap: Record<StackJustify, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
  around: 'justify-around',
  evenly: 'justify-evenly',
};

/**
 * Get wrap class based on wrap prop value
 */
function getWrapClass(wrap: StackWrap): string {
  if (wrap === true || wrap === 'wrap') return 'flex-wrap';
  if (wrap === 'wrap-reverse') return 'flex-wrap-reverse';
  if (wrap === 'nowrap') return 'flex-nowrap';
  return '';
}

/**
 * Stack Component
 *
 * A polymorphic flexbox layout component that provides consistent
 * spacing and alignment using design system tokens.
 */
export const Stack = forwardRef<HTMLElement, StackProps>(function Stack(
  {
    children,
    direction = 'column',
    gap = 'md',
    gapMd: _gapMd,
    gapLg: _gapLg,
    align = 'stretch',
    justify = 'start',
    wrap = false,
    as: Component = 'div',
    className,
    style,
    fullWidth = false,
    fullHeight = false,
    'data-testid': testId,
    ...restProps
  },
  ref
) {
  // Note: gapMd and gapLg are intentionally destructured but not used
  // to prevent them from being passed to DOM elements.
  // Use className with responsive Tailwind classes (e.g., md:gap-token-lg) instead.
  void _gapMd;
  void _gapLg;

  const combinedStyle: CSSProperties = {
    gap: gapMap[gap],
    ...style,
  };

  return (
    <Component
      ref={ref}
      className={cn(
        'flex',
        directionMap[direction],
        alignMap[align],
        justifyMap[justify],
        getWrapClass(wrap),
        fullWidth && 'w-full',
        fullHeight && 'h-full',
        className
      )}
      style={combinedStyle}
      data-testid={testId}
      {...restProps}
    >
      {children}
    </Component>
  );
});

Stack.displayName = 'Stack';

/**
 * HStack - Convenience component for horizontal stacks
 *
 * @example
 * ```tsx
 * <HStack gap="md" align="center">
 *   <Icon />
 *   <Text>Label</Text>
 * </HStack>
 * ```
 */
export const HStack = forwardRef<HTMLElement, Omit<StackProps, 'direction'>>(
  function HStack(props, ref) {
    return <Stack ref={ref} direction="row" {...props} />;
  }
);

HStack.displayName = 'HStack';

/**
 * VStack - Convenience component for vertical stacks
 *
 * @example
 * ```tsx
 * <VStack gap="lg">
 *   <Heading>Title</Heading>
 *   <Paragraph>Content...</Paragraph>
 * </VStack>
 * ```
 */
export const VStack = forwardRef<HTMLElement, Omit<StackProps, 'direction'>>(
  function VStack(props, ref) {
    return <Stack ref={ref} direction="column" {...props} />;
  }
);

VStack.displayName = 'VStack';

export default Stack;
